import { Constants } from './Constants';
import { CommonConstants, SanitizeUsername } from '@shared/common';
import { getApps, initializeApp } from 'firebase/app';
import {
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  getAuth,
  getRedirectResult,
  GoogleAuthProvider,
  onAuthStateChanged,
  reauthenticateWithCredential,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updatePassword
} from 'firebase/auth';
import type { IAuthOptions } from '../contracts/IAuthOptions';
import type { IAuthProvider } from '../contracts/IAuthProvider';
import type { IUserDTO } from '@shared/common';
import type { FirebaseApp } from 'firebase/app';
import type { Auth, User } from 'firebase/auth';

/**
 * FirebaseAuthProvider wraps the Firebase Auth SDK for authentication operations.
 *
 * - Provides login, callback handling, user info, token retrieval, and logout.
 *
 * Usage:
 *
 * - Instantiate with Firebase config, call init().
 * - Then use SignInWithPopup(), HandleRedirectCallback(), GetUser(), GetTokenSilently(), Logout().
 */

/**
 * Map a Firebase User to IUserDTO.
 *
 * @param firebaseUser - The Firebase User object.
 * @returns The mapped IUserDTO.
 */
function mapFirebaseUserToDTO(firebaseUser: User): IUserDTO {
  const rawName = firebaseUser.displayName || firebaseUser.email?.split('@')[0] || '';
  return {
    id: CommonConstants.Defaults.PendingID,
    authID: firebaseUser.uid,
    username: SanitizeUsername(rawName),
    email: firebaseUser.email || '',
    emailVerified: firebaseUser.emailVerified,
    status: CommonConstants.Database.Status.Active,
    createdAt: new Date(firebaseUser.metadata.creationTime || Date.now()),
    lastLoginAt: firebaseUser.metadata.lastSignInTime ? new Date(firebaseUser.metadata.lastSignInTime) : undefined
  };
}

/** FirebaseAuthProvider provides a wrapper around Firebase Auth SDK for authentication operations. */
export class FirebaseAuthProvider implements IAuthProvider {
  /** The Firebase app instance. */
  private app: FirebaseApp | null = null;

  /** The Firebase Auth instance. */
  private auth: Auth | null = null;

  /** Firebase client configuration options. */
  private options: IAuthOptions;

  /**
   * Create a new FirebaseAuthProvider instance.
   *
   * @param options - Firebase client configuration options.
   */
  constructor(options: IAuthOptions) {
    this.options = options;
  }

  /**
   * Check if the user is authenticated.
   *
   * Awaits `authStateReady()` first so callers invoking this on cold-load (before Firebase has finished hydrating the persisted user from IndexedDB) do not see a false negative.
   *
   * @returns Promise that resolves to a boolean indicating if the user is authenticated.
   */
  async IsAuthenticated(): Promise<boolean> {
    if (!this.auth) throw new Error(Constants.FirebaseAuthNotInitialized);
    await this.auth.authStateReady();
    return this.auth.currentUser !== null;
  }

  /**
   * Initialize the Firebase app and auth.
   *
   * @returns Promise that resolves when the client is initialized.
   */
  async Init(): Promise<void> {
    this.app = getApps()[0] ?? initializeApp(this.options);
    this.auth = getAuth(this.app);
  }

  /**
   * Start login flow with Google OAuth using popup.
   *
   * @returns Promise that resolves when the login is complete.
   */
  async SignInWithPopup(): Promise<void> {
    if (!this.auth) throw new Error(Constants.FirebaseAuthNotInitialized);
    const provider = new GoogleAuthProvider();
    // Use popup for better UX (avoids full page redirect)
    await signInWithPopup(this.auth, provider);
  }

  /**
   * Handle Firebase redirect callback after login (if using redirect flow).
   *
   * @returns Promise that resolves when the callback is handled.
   */
  async HandleRedirectCallback(): Promise<void> {
    if (!this.auth) throw new Error(Constants.FirebaseAuthNotInitialized);
    await getRedirectResult(this.auth);
  }

  /**
   * Get the authenticated user profile from Firebase and map to IUserDTO.
   *
   * Awaits `authStateReady()` first so cold-load callers do not see `undefined` while IndexedDB hydration is still in flight.
   *
   * @returns The user profile as IUserDTO, or undefined if not authenticated.
   */
  async GetUser(): Promise<IUserDTO | undefined> {
    if (!this.auth) throw new Error(Constants.FirebaseAuthNotInitialized);
    await this.auth.authStateReady();
    const firebaseUser: User | null = this.auth.currentUser;
    if (!firebaseUser) return undefined;

    return mapFirebaseUserToDTO(firebaseUser);
  }

  /**
   * Get a valid Firebase ID token silently (refreshes if needed).
   *
   * Awaits `authStateReady()` first to avoid the IndexedDB hydration race: on cold-load, early callers (e.g. a dashboard data fetch firing at boot) would otherwise see `currentUser === null` and
   * return `undefined` even though the user is in fact logged in, causing a silent redirect back to the lobby.
   *
   * @returns The Firebase ID token string, or undefined if not available.
   */
  async GetTokenSilently(): Promise<string | undefined> {
    if (!this.auth) throw new Error(Constants.FirebaseAuthNotInitialized);
    await this.auth.authStateReady();
    const user = this.auth.currentUser;
    if (!user) return undefined;
    return await user.getIdToken();
  }

  /**
   * Log out the user from Firebase.
   *
   * @returns Promise that resolves when logout is complete.
   */
  async Logout(): Promise<void> {
    if (!this.auth) throw new Error(Constants.FirebaseAuthNotInitialized);
    await signOut(this.auth);
  }

  /**
   * Sign up a new user with email and password.
   *
   * @param email - The user's email address.
   * @param password - The user's password.
   * @returns Promise that resolves when sign up is complete.
   */
  async SignUpWithEmailPassword(email: string, password: string): Promise<void> {
    if (!this.auth) throw new Error(Constants.FirebaseAuthNotInitialized);
    const credential = await createUserWithEmailAndPassword(this.auth, email, password);
    await sendEmailVerification(credential.user);
  }

  /**
   * Sign in an existing user with email and password.
   *
   * @param email - The user's email address.
   * @param password - The user's password.
   * @returns Promise that resolves when sign in is complete.
   */
  async SignInWithEmailPassword(email: string, password: string): Promise<void> {
    if (!this.auth) throw new Error(Constants.FirebaseAuthNotInitialized);
    await signInWithEmailAndPassword(this.auth, email, password);
  }

  /**
   * Send a password reset email to the user.
   *
   * @param email - The user's email address.
   * @returns Promise that resolves when the email is sent.
   */
  async SendPasswordResetEmail(email: string): Promise<void> {
    if (!this.auth) throw new Error(Constants.FirebaseAuthNotInitialized);
    await sendPasswordResetEmail(this.auth, email);
  }

  /**
   * Re-authenticate the current user with their current password and update it to the new password. Both validation (length / numeric requirement) and policy enforcement happen on the caller
   * side and on the Firebase server respectively; this method simply performs the two-step Firebase operation that allows password change without the user signing out and back in.
   *
   * @param currentPassword - The user's current password (used for re-authentication).
   * @param newPassword - The new password to set.
   * @returns Promise that resolves when the password is updated.
   * @throws Error if Firebase Auth is not initialized, no user is signed in, the user has no email on record, re-authentication fails (`auth/wrong-password`, `auth/invalid-credential`), or the
   *   new password is rejected by the Firebase password policy (`auth/weak-password`).
   */
  async UpdatePassword(currentPassword: string, newPassword: string): Promise<void> {
    if (!this.auth) throw new Error(Constants.FirebaseAuthNotInitialized);
    const user = this.auth.currentUser;
    if (!user || !user.email) throw new Error(Constants.NoUserSignedIn);
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);
  }

  /**
   * Send a verification email to the current user.
   *
   * @returns Promise that resolves when the email is sent.
   * @throws Error if Firebase Auth is not initialized or no user is signed in.
   */
  async SendEmailVerification(): Promise<void> {
    if (!this.auth) throw new Error(Constants.FirebaseAuthNotInitialized);
    const user = this.auth.currentUser;
    if (!user) throw new Error('No user signed in');
    await sendEmailVerification(user);
  }

  /**
   * Reload the current Firebase user to refresh fields like emailVerified.
   *
   * @returns The refreshed user profile as IUserDTO, or undefined if not signed in.
   * @throws Error if Firebase Auth is not initialized.
   */
  async ReloadUser(): Promise<IUserDTO | undefined> {
    if (!this.auth) throw new Error(Constants.FirebaseAuthNotInitialized);
    const user = this.auth.currentUser;
    if (!user) return undefined;
    await user.reload();
    // Force-refresh the ID token so subsequent API calls carry updated claims (e.g. email_verified)
    await user.getIdToken(true);
    return mapFirebaseUserToDTO(user);
  }

  /**
   * Listen for authentication state changes.
   *
   * @param callback - Function to call when auth state changes, receives IUserDTO or undefined.
   * @returns Unsubscribe function to stop listening.
   * @throws Error if Firebase Auth is not initialized.
   */
  OnAuthStateChanged(callback: (user: IUserDTO | undefined) => void): () => void {
    if (!this.auth) throw new Error(Constants.FirebaseAuthNotInitialized);
    return onAuthStateChanged(this.auth, async (firebaseUser) => {
      if (firebaseUser) {
        callback(mapFirebaseUserToDTO(firebaseUser));
      } else {
        callback(undefined);
      }
    });
  }
}
